import {
  canAccessModule,
  getStoredAllowedModules,
  getStoredPermissions,
  hasAnyPermission,
  hasPermissionPrefix,
  type ModuleKey,
} from "@/lib/module-access";

export interface LegalAccessContext {
  allowedModules?: ModuleKey[];
  permissions?: string[];
}

function contextModules(context?: LegalAccessContext): ModuleKey[] {
  return context?.allowedModules ?? getStoredAllowedModules();
}

function contextPermissions(context?: LegalAccessContext): string[] {
  return context?.permissions ?? getStoredPermissions();
}

export function canAccessLegalModule(context?: LegalAccessContext): boolean {
  const modules = contextModules(context);
  const permissions = contextPermissions(context);

  return (
    canAccessModule("legal", modules) &&
    hasPermissionPrefix(permissions, ["legal."])
  );
}

export function canCreateLegal(context?: LegalAccessContext): boolean {
  return hasAnyPermission(contextPermissions(context), ["legal.pdp.create"]);
}

export function canUpdateLegal(context?: LegalAccessContext): boolean {
  return hasAnyPermission(contextPermissions(context), ["legal.pdp.update"]);
}

export function canApproveLegal(context?: LegalAccessContext): boolean {
  return hasAnyPermission(contextPermissions(context), ["legal.pdp.approve"]);
}

export function canAccessLegalPath(
  pathname: string,
  context?: LegalAccessContext,
): boolean {
  if (!canAccessLegalModule(context)) return false;

  const permissions = contextPermissions(context);

  if (pathname === "/legal" || pathname === "/legal/") {
    return hasAnyPermission(permissions, ["legal.dashboard.view"]);
  }
  if (pathname.startsWith("/legal/pipeline")) {
    return hasAnyPermission(permissions, ["legal.pipeline.view"]);
  }
  if (pathname.startsWith("/legal/pdp-data")) {
    return hasAnyPermission(permissions, ["legal.pdp_data.view"])
      && (!pathname.includes("/upload") || hasAnyPermission(permissions, ["legal.pdp_data.upload"]));
  }
  if (pathname.startsWith("/legal/pdp")) {
    return hasAnyPermission(
      permissions,
      pathname.includes("/create") ? ["legal.pdp.create"] : ["legal.pdp.view"],
    );
  }
  if (pathname.startsWith("/legal/workspaces/approvals")) {
    return hasAnyPermission(permissions, ["legal.contracts.view", "legal.legality.view", "legal.letters.view"])
      && hasAnyPermission(permissions, ["legal.contracts.approve", "legal.legality.approve", "legal.letters.approve", "legal.contracts.reject", "legal.legality.reject", "legal.letters.reject"]);
  }
  if (pathname.startsWith("/legal/workspaces/submissions")) {
    return hasAnyPermission(permissions, ["legal.contracts.view", "legal.legality.view", "legal.letters.view"]);
  }
  for (const category of ["contracts", "legality", "letters"]) {
    const segments = pathname.split("/").filter(Boolean);
    const index = segments.indexOf(category);
    if (index < 0) continue;
    const tail = segments.slice(index + 1);
    const action = tail.includes("create") ? "create" : tail.length ? "update" : null;
    return hasAnyPermission(permissions, [`legal.${category}.view`])
      && (!action || hasAnyPermission(permissions, [`legal.${category}.${action}`]));
  }
  for (const resource of ["banks", "contacts", "customers", "partners", "vendors"]) {
    if (pathname === `/legal/${resource}` || pathname.startsWith(`/legal/${resource}/`)) {
      const action = pathname.endsWith("/create") ? "create" : pathname.endsWith("/edit") ? "update" : null;
      return hasAnyPermission(permissions, [`legal.${resource}.view`])
        && (!action || hasAnyPermission(permissions, [`legal.master_data.${action}`]));
    }
  }
  if (pathname.startsWith("/legal/activity-logs")) {
    return hasAnyPermission(permissions, ["legal.activity_logs.view"]);
  }
  if (pathname.startsWith("/legal/companies")) {
    return hasAnyPermission(permissions, ["legal.master_data.view"]);
  }

  return hasAnyPermission(permissions, ["legal.legality.view"]);
}
